import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Injector,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core'
import {FileService} from '../file.service'
import {FileModel} from '../file.model'
import {takeUntil} from 'rxjs/operators'
import {Subject} from 'rxjs'
import {NG_VALUE_ACCESSOR} from '@angular/forms'
import {ControlValueAccessorConnectorDirective} from '../../forms/control-value-accessor-connector.directive'
import {Logger} from '../../../core/logging/logger.types'
import {GenerateFileUploadLinkFunction} from '../file.service.types'

@Component({
  selector: 'cft-file-input',
  templateUrl: './file-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileInputComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class FileInputComponent extends ControlValueAccessorConnectorDirective implements OnDestroy {
  /**
   * Defines what file types the user can pick from the file input dialog.
   * You can provide one or multiple of the following options:
   * - A valid MIME type string, with no extensions.
   * - The string image/* meaning "any image file".
   * - The string audio/* meaning "any audio file".
   * - The string video/* meaning "any video file".
   */
  accept = input<string[]>([])
  protected acceptString = computed(() => this.accept().join(','))
  /** allows to select multiple files */
  multiple = input(false, {transform: booleanAttribute})
  /** html ID */
  id = input<string | undefined>()

  /**
   * This property is used to generate a file upload link for each file selected.
   * When this input is provided, files are automatically uploaded when selected.
   * If you don't want auto-upload, listen to the output `filesSelected` to manage the upload yourself, and leave this input undefined.
   */
  generateUploadLink = input<GenerateFileUploadLinkFunction | undefined>()

  /** Emits when files are selected */
  filesSelected = output<{file: FileModel; blob: File}[]>()
  /**
   * Emits when a file is successfully uploaded
   * With multiple, this will emit multiple times
   * This won't emit for errors, use `filesSelected` for that, and manage the upload yourself with FileService.upload
   */
  fileUploaded = output<FileModel>()

  protected fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput')
  private readonly unsubscribe$ = new Subject<void>()

  constructor(
    injector: Injector,
    private readonly fileService: FileService,
    private readonly logger: Logger,
  ) {
    super(injector)
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  get selectedFiles(): FileModel[] {
    return this.control?.value || []
  }

  click() {
    this.fileInput().nativeElement.click()
  }

  async onFileInputChange(e: Event) {
    const target = e.target as HTMLInputElement
    const files = target?.files

    if (files) {
      const currentSelection: {file: FileModel; blob: File}[] = []
      const selectedFiles: FileModel[] = this.control?.value ?? []

      for (const blob of Array.from(files)) {
        try {
          const file = await this.fileService.parseBlob(blob)
          selectedFiles.push(file)
          currentSelection.push({file, blob})
          this.uploadFile(file, blob)
        } catch (err) {
          this.logger.error(err)
        }
      }
      this.filesSelected.emit(currentSelection)
      this.control?.setValue(selectedFiles)

      // reset to allow selecting the same file again
      target.value = ''
    }
  }

  private uploadFile(file: FileModel, blob: File) {
    const generateUploadLink = this.generateUploadLink()
    if (!generateUploadLink || file.uploaded) {
      // no auto-upload, or file already uploaded
      return
    }

    this.fileService
      .uploadFile(file, blob, generateUploadLink)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: file => {
          if (file.uploaded) {
            this.fileUploaded.emit(file)
          }
          this.control?.setValue(this.selectedFiles) // trigger valueChanges event
        },
        error: err => {
          this.logger.error(`An error occurred while uploading file`, err)
          file.error = true
          file.uploaded = false
          this.control?.setValue(this.selectedFiles) // trigger valueChanges event
        },
      })
  }
}
